import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    signal,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25TableModule } from "../../modules/s25-table/s25.table.module";
import { Table } from "../../modules/s25-table/Table";
import { S25TableComponent } from "../../modules/s25-table/s25.table.component";
import { GenericTableButtonComponent } from "../../modules/s25-table/generics/generic.table.button.component";
import { WaitlistStore } from "../../stores/waitlist.store";
import {
    WaitlistCreatRequest,
    WaitlistEntry,
    WaitlistReorderWaitingEntry,
    WaitlistRequest,
    WaitingEntry,
    WaitingEntryRequest,
} from "../../pojo/Waitlist";
import { S25Util } from "../../util/s25-util";
import { Bind } from "../../decorators/bind.decorator";
import { S25InlineDatepickerComponent } from "../../standalone/s25.inline.datepicker.component";
import { FormsModule } from "@angular/forms";
import { AvailWeekly } from "../../modules/s25-avail-weekly/s25.avail.weekly.util";
import { S25ItemModule } from "../../modules/s25-item/s25.item.module";
import { S25ItemComponent } from "../../modules/s25-item/s25.item.component";
import { Item } from "../../pojo/Item";
import { S25Datefilter } from "../../modules/s25-dateformat/s25.datefilter.service";
import { s25Resource } from "../../util/s25.resource";
import { UserprefService } from "../../services/userpref.service";
import { WaitlistGroupToggleComponent } from "./s25.waitlist.group.toggle.component";
import { WaitlistPositionEditorComponent } from "./s25.waitlist.position.editor.component";
import { S25ModalModule } from "../../modules/s25-modal/s25.modal.module";
import { S25ModalComponent } from "../../modules/s25-modal/s25.modal.component";
import { S25TableUtil } from "../../modules/s25-table/s25.table.util";
import { EventMicroService } from "../../services/event.micro.service";
import { EventService } from "../../services/event.service";
import { Event } from "../../pojo/Event";
import { Contact } from "../../pojo/Contact";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25ButtonComponent } from "../s25.button.component";
import { ManageTaskComponent } from "../../modules/s25-task/manage.tasks.component";
import { FlsService } from "../../services/fls.service";
import { TaskCompetingUtil } from "../../modules/s25-task/task.competing.util";
import { EventReqInfo } from "../../services/avail.service";
import { AccessLevels, hasEditFls } from "../../pojo/Fls";
import { TelemetryService } from "../../services/telemetry.service";
import { ReservationService, EventReservation } from "../../services/reservation.service";
import { UserStore } from "../../stores/user.store";
interface FlatWaitingRow {
    groupKey: string;
    entry: WaitlistEntry;
    waiting: WaitingEntry;
}

/** Same value as `Table.Row.id` and `data-s25-row-id` on the table `<tr>`. */
function waitlistWaitingRowTableId(groupKey: string, rsrvId: number): string {
    return `${groupKey}-${rsrvId}`;
}

function findWaitlistWaitingRowElement(
    tableHost: HTMLElement,
    groupKey: string,
    rsrvId: number,
): HTMLTableRowElement | null {
    const id = waitlistWaitingRowTableId(groupKey, rsrvId);
    return tableHost.querySelector<HTMLTableRowElement>(`tr[data-s25-row-id="${CSS.escape(id)}"]`);
}

@TypeManagerDecorator("s25-ng-waitlist")
@Component({
    selector: "s25-ng-waitlist",
    template: `
        <div class="optbar">
            @if (showManageWaitlist()) {
                <s25-ng-button [type]="'outline'" [onClick]="manageWaitlistModal()?.open">
                    Manage Waitlist
                    <s25-ng-icon [type]="'queue'" [ariaLabel]="false" />
                </s25-ng-button>
            }

            @if (showDateControls()) {
                <s25-ng-inline-datepicker
                    [(firstDate)]="firstDateModel"
                    [weeks]="weeksModel()"
                    (firstDateChange)="onDateChange($event)"
                />
                <label class="weeks">
                    <span>Weeks:</span>
                    <select class="cn-form__control" [ngModel]="weeksModel()" (ngModelChange)="onWeeksChange($event)">
                        @for (week of weekOptions; track week) {
                            <option [ngValue]="week">{{ week }}</option>
                        }
                    </select>
                </label>
            }
        </div>
        @if (showManageWaitlist()) {
            <s25-ng-modal #manageWaitlistModal [title]="'Tasks By Location'" [size]="'xl'">
                <ng-template #s25ModalBody>
                    <s25-ng-manage-task
                        [data]="{
                            waitlistEvents: waitlistEvents,
                            itemIds: effectiveItemIds(),
                            dateRang: { startDt: startDate(), endDt: endDate() },
                        }"
                    />
                </ng-template>
            </s25-ng-modal>
        }
        <s25-ng-table
            [dataSource]="tableData"
            [columnSortable]="true"
            [hasFilter]="true"
            [hasRefresh]="true"
            [unlimitedWidth]="true"
            [caption]="'Waitlist'"
            [noResultsMessage]="'No waitlist entries found for this date range.'"
        />
        <s25-ng-modal #deleteModal [title]="'Confirm Deletion'" [type]="'deny'" [size]="'xs'">
            {{ deleteMessage() }}
        </s25-ng-modal>
        <s25-ng-modal #addEventModal [title]="'Add Event to Waitlist'" [size]="'sm'">
            <ng-template #s25ModalBody>
                <div class="add-event-form">
                    <div class="add-event-row">
                        <label class="add-event-input">
                            Event Reference <span class="c-required"> * </span>
                            <input
                                class="cn-form__control"
                                [ngModel]="eventLocatorInput()"
                                (ngModelChange)="eventLocatorInput.set($event)"
                                placeholder="e.g. 2026-AARQPP"
                            />
                        </label>
                        <s25-ng-button [type]="'primary'" [onClick]="searchEventByLocator">Search & Add</s25-ng-button>
                    </div>
                    @if (searchError()) {
                        <div class="ngDanger">{{ searchError() }}</div>
                    }
                    @if (foundEvent(); as event) {
                        <div class="found-event-result">
                            <s25-item
                                [includeTypeIcon]="true"
                                [itemTypeId]="1"
                                [itemId]="event.id"
                                [itemName]="event.name"
                            />
                        </div>
                    }
                </div>
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        :host {
            display: block;
        }

        .optbar {
            display: flex;
            padding-block: 0.25rem;
            gap: 0.5rem;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
        }

        .weeks {
            display: flex;
            align-items: center;
            gap: 0.25em;
        }

        ::ng-deep .waitlist-group-header {
            background-color: #f0f0f0;
            font-weight: bold;
        }

        :host-context(.nm-party--on) ::ng-deep .waitlist-group-header {
            background-color: #3c3d46;
        }

        ::ng-deep .waitlist-group-header .aw-button {
            white-space: nowrap;
        }

        ::ng-deep .waitlist-tooltip-request,
        ::ng-deep .waitlist-tooltip-no-request {
            position: relative;
            cursor: help;
            color: var(--link-color, #0563c1);
            text-decoration: underline dotted;
            text-underline-offset: 2px;
        }

        ::ng-deep .waitlist-tooltip-no-request svg {
            opacity: 0.5;
            filter: grayscale(1);
        }

        ::ng-deep .waitlist-tooltip-request:hover::after,
        ::ng-deep .waitlist-tooltip-no-request:hover::after {
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background: #333;
            color: #fff;
            padding: 0.35em 0.65em;
            border-radius: 4px;
            font-size: 0.85em;
            white-space: normal;
            max-width: 12em;
            width: max-content;
            text-align: center;
            z-index: 10;
            pointer-events: none;
        }

        ::ng-deep .waitlist-tooltip-request:hover::after {
            content: "Location has been requested and is pending approval.";
        }

        ::ng-deep .waitlist-tooltip-no-request:hover::after {
            content: "Location has been added to waitlist.";
        }

        ::ng-deep .waitlist-location-assigned,
        ::ng-deep .waitlist-location-requested {
            position: relative;
            cursor: help;
        }

        ::ng-deep .waitlist-location-assigned s25-ng-icon {
            color: #0563c1;
        }

        ::ng-deep .waitlist-location-requested s25-ng-icon {
            opacity: 0.5;
            filter: grayscale(1);
        }

        ::ng-deep .waitlist-location-assigned:hover::after,
        ::ng-deep .waitlist-location-requested:hover::after {
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background: #333;
            color: #fff;
            padding: 0.35em 0.65em;
            border-radius: 4px;
            font-size: 0.85em;
            white-space: normal;
            max-width: 14em;
            width: max-content;
            text-align: center;
            z-index: 10;
            pointer-events: none;
        }

        ::ng-deep .waitlist-location-assigned:hover::after {
            content: "This location is assigned to this event.";
        }

        ::ng-deep .waitlist-location-requested:hover::after {
            content: "This location is requested to this event.";
        }

        .add-event-form {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            padding-bottom: 3rem;
            padding-top: 1rem;
        }

        .add-event-row {
            display: flex;
            align-items: flex-end;
            gap: 0.25rem;
        }

        .add-event-input {
            flex: 0.75;
        }

        .found-event-result {
            padding: 0.5rem;
            border: 1px solid var(--s25-border-color, #ccc);
            border-radius: 4px;
        }

        ::ng-deep tr.waitlist-row-flipping {
            position: relative;
            z-index: 2;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        S25TableModule,
        S25InlineDatepickerComponent,
        FormsModule,
        S25ItemModule,
        S25ModalModule,
        S25IconComponent,
        ManageTaskComponent,
        S25ButtonComponent,
    ],
})
export class WaitlistComponent {
    readonly itemId = input<number>();
    readonly waitlistId = input<number>();
    readonly groupKey = input<string>();
    readonly startDt = input<Date | string>();
    readonly weeks = input<number>(1);

    readonly showDateControls = computed(() => !this.waitlistId() && !this.groupKey());

    private readonly waitlistStore = inject(WaitlistStore);
    private readonly userStore = inject(UserStore);

    private readonly tableComponent = viewChild(S25TableComponent);
    private readonly deleteModal = viewChild<S25ModalComponent>("deleteModal");
    readonly manageWaitlistModal = viewChild<S25ModalComponent>("manageWaitlistModal");
    readonly deleteMessage = signal("");
    private readonly addEventModal = viewChild<S25ModalComponent>("addEventModal");
    readonly eventLocatorInput = signal("");
    readonly searchError = signal("");
    readonly searchSuccess = signal("");
    readonly foundEvent = signal<{ id: number; name: string } | null>(null);
    private activeWaitlistEntry: WaitlistEntry | null = null;
    readonly showManageWaitlist = computed(() => !!this._manageWaitlistPerms.value());
    private readonly _fls = s25Resource({ loader: () => FlsService.getFls() });
    readonly canEditWaitlist = computed(() => hasEditFls(this._fls.value()?.SPACE_WAITLIST));
    readonly effectiveItemIds = computed(() => {
        const id = this.itemId() ?? this.cachedEntries[0]?.spaceId;
        return id ? [id] : [];
    });
    waitlistEvents: EventReqInfo[] = [];

    readonly weekOptions = AvailWeekly.WEEK_OPTIONS;

    readonly firstDateModel = linkedSignal(() => {
        const raw = this.startDt();
        return raw ? S25Util.date.getDate(raw) : new Date();
    });

    readonly weeksModel = linkedSignal(() => this.weeks());

    /** Matches `S25InlineDatepickerComponent` + weekly waitlist queries: week-aligned start through end of last day. */
    readonly startDate = computed(() =>
        S25Util.date.firstDayOfWeek(S25Util.date.getDate(this.firstDateModel()), this.userStore.weekstart()),
    );

    readonly endDate = computed(() => {
        const weekStart = this.userStore.weekstart();
        const start = this.startDate();
        const weeks = this.weeksModel();
        const lastWeekAnchor = weeks <= 1 ? start : S25Util.date.addWeeks(start, weeks - 1);
        return S25Util.date.toEndOfDay(S25Util.date.lastDayOfWeek(lastWeekAnchor, weekStart));
    });

    tableData: Table.Unpaginated = {
        type: "unpaginated",
        dataSource: this.getRows,
        columns: [
            { id: "waitlistId", header: "", width: 15, align: "center", sortable: false },
            { id: "blockingEvent", header: "Blocking Event", width: 100 },
            { id: "locationStatus", header: "", width: 30, sortable: false },
            { id: "eventName", header: "Event Name", width: 100 },
            { id: "eventLocator", header: "Reference", width: 100 },
            { id: "startDt", header: "Start", type: "date", width: 100 },
            { id: "endDt", header: "End", type: "date", width: 100 },
            { id: "scheduler", header: "Scheduler" },
            { id: "requestor", header: "Requestor" },
            { id: "organization", header: "Organization" },
            { id: "position", header: "Position", align: "center", sortable: false },
            //  { id: "isRequest", header: "Has Request Task", width: 100, align: "center", sortable: false },  // commented out for now
            GenericTableButtonComponent.Column(
                "Delete",
                this.onDelete.bind(this),
                "danger--outline",
                "min-content",
                false,
                "",
            ),
            { id: "addEvent", header: "", sortable: false },
        ],
        customSort: (rowA, rowB, column, order) => {
            const aIsHeader = rowA.className === "waitlist-group-header";
            const bIsHeader = rowB.className === "waitlist-group-header";
            const aGroup = aIsHeader
                ? (rowA.id as string).slice("header-".length)
                : (rowA.data as FlatWaitingRow)?.groupKey;
            const bGroup = bIsHeader
                ? (rowB.id as string).slice("header-".length)
                : (rowB.data as FlatWaitingRow)?.groupKey;

            if (aGroup !== bGroup) {
                if (column.id === "blockingEvent") {
                    const aHeader = this.cachedEntries.find((e) => e.groupKey === aGroup);
                    const bHeader = this.cachedEntries.find((e) => e.groupKey === bGroup);
                    const aName = aHeader?.blockingEventName ?? "";
                    const bName = bHeader?.blockingEventName ?? "";
                    const cmp = aName.localeCompare(bName);
                    return order === "asc" ? -cmp : cmp;
                }
                const aIdx = this.cachedEntries.findIndex((e) => e.groupKey === aGroup);
                const bIdx = this.cachedEntries.findIndex((e) => e.groupKey === bGroup);
                return aIdx - bIdx;
            }

            if (aIsHeader) return -1;
            if (bIsHeader) return 1;

            return S25TableUtil.sortRows(rowA, rowB, column, order);
        },
    };

    readonly dateTimeFormat = s25Resource({ loader: () => UserprefService.getS25DateTimeformat() });

    private readonly _manageWaitlistPerms = s25Resource({
        loader: async () => {
            const [fls, waitlistOn] = await Promise.all([
                FlsService.getFls(),
                TaskCompetingUtil.waitlistEnabled(undefined, null, true),
            ]);
            return (
                waitlistOn &&
                fls?.TASK_LIST === AccessLevels.Full &&
                fls?.VIEW_OTHERS === AccessLevels.Full &&
                !S25Util.isInIframe
            );
        },
    });

    private readonly dirtyGroupKeys = signal(new Set<string>());

    private markDirty(groupKey: string) {
        this.dirtyGroupKeys.update((s) => new Set(s).add(groupKey));
    }

    private clearDirty(groupKey: string) {
        this.dirtyGroupKeys.update((s) => {
            const ns = new Set(s);
            ns.delete(groupKey);
            return ns;
        });
    }

    private collapsedGroups = new Set<string>();
    private blockingEventPositions = new Map<string, number>();
    private cachedEntries: WaitlistEntry[] = [];
    private eventDetailMap = new Map<
        number,
        { eventLocator?: string; scheduler?: string; requestor?: string; organization?: string }
    >();
    private reservationMap = new Map<number, EventReservation>();
    /** Bumps when table data reloads; cancels in-flight row reorder FLIP animations. */
    private waitlistRowMoveAnimGeneration = 0;
    /** True while refetching after snapping the date range to the first group's week (avoids infinite loop). */
    private aligningDisplayedWeekToEntries = false;

    /** Earliest waiting reservation start among the first sorted group (matches top of table). */
    private earliestWaitingStartInFirstGroup(sorted: WaitlistEntry[]): Date | null {
        const waiting = sorted[0]?.waiting;
        if (!waiting?.length) return null;
        let min: Date | null = null;
        for (const w of waiting) {
            const dt = S25Util.date.parse(w.startDt);
            if (isNaN(dt.getTime())) continue;
            if (!min || dt < min) min = dt;
        }
        return min;
    }

    @Bind
    async getRows(query?: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        if (!this.aligningDisplayedWeekToEntries) {
            this.waitlistRowMoveAnimGeneration++;
        }
        const waitlistId = this.waitlistId();
        const groupKey = this.groupKey();
        const spaceId = this.itemId();

        let entries: WaitlistEntry[];
        if (waitlistId) {
            entries = await this.waitlistStore.getWaitlistEntries(
                { waitlist_id: waitlistId, include_requests: "all_competes" },
                true,
            );
        } else {
            if (!spaceId) return { rows: [] };
            entries = await this.waitlistStore.getWaitlistEntries(
                {
                    space_id: spaceId,
                    start_dt: this.startDate(),
                    end_dt: this.endDate(),
                    include_requests: "all_competes",
                },
                true,
            );
            if (groupKey) {
                entries = entries.filter((entry) => entry.groupKey === groupKey);
            }
        }

        const filteredSorted = entries
            .filter((e) => e.waiting?.length)
            .sort((a, b) => a.groupKey.localeCompare(b.groupKey));

        if (!waitlistId && !groupKey && spaceId && !this.aligningDisplayedWeekToEntries && filteredSorted.length > 0) {
            const suggested = this.earliestWaitingStartInFirstGroup(filteredSorted);
            if (suggested) {
                const ws = this.userStore.weekstart();
                const anchor = S25Util.date.firstDayOfWeek(S25Util.date.getDate(suggested), ws);
                if (!S25Util.date.equalDate(anchor, this.startDate())) {
                    this.firstDateModel.set(anchor);
                    this.aligningDisplayedWeekToEntries = true;
                    try {
                        return await this.getRows(query);
                    } finally {
                        this.aligningDisplayedWeekToEntries = false;
                    }
                }
            }
        }

        this.cachedEntries = filteredSorted.map((e) => ({ ...e, waiting: e.waiting.map((w) => ({ ...w })) }));
        this.dirtyGroupKeys.set(new Set());
        this.blockingEventPositions.clear();

        const [eventDetailsOk] = await Promise.all([this.fetchEventDetails(), this.fetchReservations()]);
        if (!eventDetailsOk) return { rows: [] };
        const effectiveSpaceId = spaceId ?? this.cachedEntries[0]?.spaceId;
        if (effectiveSpaceId) this.buildWaitlistEvents(effectiveSpaceId);

        return { rows: this.cachedEntries.flatMap(this.mapEntryToRows) };
    }

    private async fetchEventDetails(): Promise<boolean> {
        const eventIds = new Set<number>();
        for (const entry of this.cachedEntries) {
            if (entry.blockingEventId) eventIds.add(entry.blockingEventId);
            for (const w of entry.waiting ?? []) if (w.eventId) eventIds.add(w.eventId);
        }
        if (!eventIds.size) return true;

        const [resp, error] = await S25Util.Maybe(
            EventMicroService.getEventDetailById([...eventIds], true, ["roles", "organizations"]),
        );
        if (error) {
            S25Util.showError(error);
            return false;
        }
        const items = resp?.content?.data?.items ?? [];
        const contacts = resp?.content?.expandedInfo?.contacts ?? [];
        const orgs = resp?.content?.expandedInfo?.organizations ?? [];
        const contactMap = new Map(
            contacts.map((c) => [c.contactId, c.fullName ?? `${c.firstName ?? ""} ${c.familyName ?? ""}`.trim()]),
        );
        const orgMap = new Map(orgs.map((o) => [o.organizationId, o.organizationName ?? ""]));

        this.eventDetailMap.clear();
        for (const item of items) {
            if (!item.id) continue;
            const roles = item.roles ?? [];
            const schedulerRole = roles.find((r) => r.roleId === Contact.Role.Event.id.Scheduler);
            const requestorRole = roles.find((r) => r.roleId === Contact.Role.Event.id.Requestor);
            const primaryOrgId = item.organizations?.primary;

            this.eventDetailMap.set(item.id, {
                eventLocator: item.eventLocator,
                scheduler: schedulerRole?.contactId ? contactMap.get(schedulerRole.contactId) : undefined,
                requestor: requestorRole?.contactId ? contactMap.get(requestorRole.contactId) : undefined,
                organization: primaryOrgId ? orgMap.get(primaryOrgId) : undefined,
            });
        }
        return true;
    }

    private async fetchReservations() {
        const rsrvIds = new Set<number>();
        for (const entry of this.cachedEntries) {
            if (entry.blockingRsrvId) rsrvIds.add(entry.blockingRsrvId);
            for (const w of entry.waiting ?? []) if (w.rsrvId) rsrvIds.add(w.rsrvId);
        }
        if (!rsrvIds.size) return;

        const result = await ReservationService.getReservationsByRsrvIds([...rsrvIds], "minimal", ["requests"]);
        const reservations = S25Util.array.forceArray(result);

        this.reservationMap.clear();
        for (const rsrv of reservations) {
            if (rsrv?.reservation_id) {
                this.reservationMap.set(rsrv.reservation_id, rsrv);
            }
        }
    }

    private buildWaitlistEvents(spaceId: number) {
        this.waitlistEvents = this.cachedEntries.flatMap((entry) => {
            const locationRequested =
                this.isBlockingLocationAssigned(entry) === false && !!entry.blockingEventId && !!entry.blockingRsrvId;
            const blockingPos = locationRequested ? (this.blockingEventPositions.get(entry.groupKey) ?? 1) : 0;
            const totalPositions = (entry.waiting?.length ?? 0) + (locationRequested ? 1 : 0);

            const waitingPart: EventReqInfo[] = (entry.waiting ?? []).map((w) => ({
                itemId: w.eventId,
                itemName: w.eventName,
                rsrvId: w.rsrvId,
                eventDate: w.startDt,
                objectId: spaceId,
                waitlistOrder: locationRequested && w.position >= blockingPos ? w.position + 1 : w.position,
                competingRsrvCount: totalPositions,
            }));

            if (locationRequested) {
                const blockingRsrv = this.reservationMap.get(entry.blockingRsrvId);
                waitingPart.push({
                    itemId: entry.blockingEventId,
                    itemName: entry.blockingEventName,
                    rsrvId: entry.blockingRsrvId,
                    eventDate: blockingRsrv?.event_start_dt,
                    objectId: spaceId,
                    waitlistOrder: blockingPos,
                    competingRsrvCount: totalPositions,
                    isBlockingEvent: true,
                });
            }

            return waitingPart;
        });
    }

    private isBlockingLocationAssigned(entry: WaitlistEntry): boolean | null {
        const reservation = this.reservationMap.get(entry.blockingRsrvId);
        const spaceId = S25Util.parseInt(this.itemId() ?? entry.spaceId);
        if (!reservation || !spaceId) return null;

        const spaceReservations = S25Util.array.forceArray(reservation.space_reservation);
        return spaceReservations.some(
            (sr) => S25Util.parseInt(sr.space_id) === spaceId && sr.space_status === "ASSIGNED",
        );
    }

    private hasSpaceReservation(rsrvId: number, spaceId: number): boolean {
        const reservation = this.reservationMap.get(rsrvId);
        if (!reservation) return false;
        const parsedSpaceId = S25Util.parseInt(this.itemId() ?? spaceId);
        if (!parsedSpaceId) return false;
        const spaceReservations = S25Util.array.forceArray(reservation.space_reservation);
        return spaceReservations.some((sr) => S25Util.parseInt(sr.space_id) === parsedSpaceId);
    }

    private getLocationStatusCell(entry: WaitlistEntry): Table.Cell {
        const isAssigned = this.isBlockingLocationAssigned(entry);
        if (isAssigned === null) return { text: "" };

        return {
            component: S25IconComponent,
            inputs: {
                type: "location",
                ariaLabel: isAssigned ? "waitlist location assigned" : "waitlist location requested",
            },
            className: isAssigned ? "waitlist-location-assigned" : "waitlist-location-requested",
        } satisfies Table.Cell<S25IconComponent>;
    }

    toggleGroup(groupKey: string) {
        if (this.collapsedGroups.has(groupKey)) {
            this.collapsedGroups.delete(groupKey);
        } else {
            this.collapsedGroups.add(groupKey);
        }

        const table = this.tableComponent();
        if (!table) return;
        table.rows = this.cachedEntries.flatMap(this.mapEntryToRows);
        table.applyFilter(table.filterValue);
        table.detectChanges();
    }

    @Bind
    private mapEntryToRows(entry: WaitlistEntry): Table.Row[] {
        const waiting = entry.waiting ?? [];
        const collapsed = this.collapsedGroups.has(entry.groupKey);
        const earliestStart = waiting.reduce(
            (min, w) => {
                const dt = S25Util.date.parse(w.startDt);
                return !min || dt < min ? dt : min;
            },
            null as Date | null,
        );

        const isAssigned = this.isBlockingLocationAssigned(entry);
        const locationRequested = isAssigned === false;
        const blockingPos = locationRequested ? (this.blockingEventPositions.get(entry.groupKey) ?? 1) : 0;
        const totalPositions = locationRequested ? waiting.length + 1 : waiting.length;
        const blockingAtOne = !locationRequested || blockingPos === 1;

        const sorted = [...waiting].sort((a, b) => a.position - b.position);
        const headerEvent = blockingAtOne ? null : sorted[0];
        const headerEventId = headerEvent?.eventId ?? entry.blockingEventId;
        const headerEventName = headerEvent?.eventName ?? entry.blockingEventName;
        const headerDetail = this.eventDetailMap.get(headerEventId);
        const headerRsrv = this.reservationMap.get(blockingAtOne ? entry.blockingRsrvId : headerEvent!.rsrvId);
        const firstWaitingRsrv = sorted[0] ? this.reservationMap.get(sorted[0].rsrvId) : undefined;

        const dateTimeFormat = this.dateTimeFormat.value() ?? "";
        const headerRow: Table.Row = {
            id: `header-${entry.groupKey}`,
            name: entry.waitlistId != null ? `Waitlist #${entry.waitlistId}` : entry.blockingEventName,
            className: "waitlist-group-header",
            cells: {
                waitlistId: {
                    component: WaitlistGroupToggleComponent,
                    inputs: {
                        collapsed,
                        waitlistId: entry.waitlistId,
                    },
                    outputs: {
                        toggle: () => this.toggleGroup(entry.groupKey),
                    },
                },
                blockingEvent: {
                    component: S25ItemComponent,
                    inputs: {
                        includeTypeIcon: true,
                        itemTypeId: Item.Ids.Event,
                        itemId: headerEventId,
                        itemName: headerEventName,
                    },
                    textValue: headerEventName,
                },
                locationStatus: this.getLocationStatusCell(entry),
                eventLocator: {
                    text: headerEvent?.eventLocator ?? headerRsrv?.event_locator ?? headerDetail?.eventLocator ?? "",
                },
                ...(locationRequested
                    ? {
                          position: blockingAtOne
                              ? this.canEditWaitlist()
                                  ? {
                                        component: WaitlistPositionEditorComponent,
                                        inputs: {
                                            position: computed(() => {
                                                this.dirtyGroupKeys();
                                                return this.blockingEventPositions.get(entry.groupKey) ?? 1;
                                            }),
                                            maxPosition: totalPositions,
                                        },
                                        outputs: {
                                            positionChange: (newPos: number) =>
                                                this.onBlockingPositionChange(entry, newPos),
                                        },
                                    }
                                  : { text: String(blockingPos) }
                              : {
                                    component: WaitlistPositionEditorComponent,
                                    inputs: { position: 1, maxPosition: totalPositions },
                                    outputs: {
                                        positionChange: (newPos: number) => {
                                            if (!headerEvent || newPos === 1) return;
                                            const bp = this.blockingEventPositions.get(entry.groupKey) ?? 1;
                                            if (newPos === bp) {
                                                this.blockingEventPositions.set(entry.groupKey, 1);
                                                this.markDirty(entry.groupKey);
                                                const internalPos = newPos < 1 ? newPos : newPos - 1;
                                                if (internalPos !== headerEvent.position) {
                                                    this.onHeaderPositionChange(entry, headerEvent, internalPos);
                                                } else {
                                                    this.rebuildTableRows();
                                                }
                                            } else {
                                                const internalPos = newPos > bp ? newPos - 1 : newPos;
                                                this.onHeaderPositionChange(entry, headerEvent, internalPos);
                                            }
                                        },
                                    },
                                },
                      }
                    : {}),
                startDt: this.waitlistDateCell(
                    headerRsrv?.event_start_dt || headerEvent?.startDt || firstWaitingRsrv?.event_start_dt,
                    dateTimeFormat,
                ),
                endDt: this.waitlistDateCell(
                    headerRsrv?.event_end_dt || headerEvent?.endDt || firstWaitingRsrv?.event_end_dt,
                    dateTimeFormat,
                ),
                scheduler: { text: headerDetail?.scheduler ?? "" },
                requestor: { text: headerDetail?.requestor ?? "" },
                organization: { text: headerDetail?.organization ?? "" },
                ...(this.canEditWaitlist()
                    ? {
                          delete: {
                              component: GenericTableButtonComponent,
                              inputs: {
                                  label: "Save Position Order",
                                  type: "primary",
                                  disabled: computed(() => !this.dirtyGroupKeys().has(entry.groupKey)),
                                  onClick: () => this.savePositionOrder(entry),
                              },
                          } satisfies Table.Cell<GenericTableButtonComponent>,
                          addEvent: {
                              component: GenericTableButtonComponent,
                              inputs: {
                                  label: "Add Event to Waitlist",
                                  type: "outline",
                                  onClick: () => this.openAddEventModal(entry),
                              },
                          } satisfies Table.Cell<GenericTableButtonComponent>,
                      }
                    : { delete: { text: "", component: undefined }, addEvent: { text: "" } }),
            },
        };

        if (collapsed) return [headerRow];

        const childWaiting = blockingAtOne ? sorted : sorted.slice(1);
        const childRows: Table.Row[] = childWaiting.map((w) => {
            const detail = this.eventDetailMap.get(w.eventId);
            const waitingRsrv = this.reservationMap.get(w.rsrvId);
            const displayPos = computed(() => {
                this.dirtyGroupKeys();
                const bp = this.blockingEventPositions.get(entry.groupKey) ?? 1;
                return locationRequested && w.position >= bp ? w.position + 1 : w.position;
            });
            return {
                id: waitlistWaitingRowTableId(entry.groupKey, w.rsrvId),
                name: w.eventName,
                data: { groupKey: entry.groupKey, entry, waiting: w } as FlatWaitingRow,
                cells: {
                    eventName: {
                        component: S25ItemComponent,
                        inputs: {
                            includeTypeIcon: true,
                            itemTypeId: Item.Ids.Event,
                            itemId: w.eventId,
                            itemName: w.eventName,
                        },
                        textValue: w.eventName,
                    },
                    eventLocator: { text: w.eventLocator || waitingRsrv?.event_locator || detail?.eventLocator || "" },
                    startDt: this.waitlistDateCell(waitingRsrv?.event_start_dt || w.startDt, dateTimeFormat),
                    endDt: this.waitlistDateCell(waitingRsrv?.event_end_dt || w.endDt, dateTimeFormat),
                    scheduler: { text: detail?.scheduler ?? "" },
                    requestor: { text: detail?.requestor ?? "" },
                    organization: { text: detail?.organization ?? "" },
                    position: this.canEditWaitlist()
                        ? {
                              component: WaitlistPositionEditorComponent,
                              inputs: { position: displayPos, maxPosition: totalPositions },
                              outputs: {
                                  positionChange: (newDisplayPos: number, context: Table.CellCallbackContext) => {
                                      const bp = this.blockingEventPositions.get(entry.groupKey) ?? 1;
                                      if (locationRequested && newDisplayPos === bp) {
                                          const flat = context.row.data as FlatWaitingRow;
                                          const oldDisplay =
                                              flat.waiting.position >= bp
                                                  ? flat.waiting.position + 1
                                                  : flat.waiting.position;
                                          this.blockingEventPositions.set(entry.groupKey, oldDisplay);
                                          const newBp = oldDisplay;
                                          const newInternal = newDisplayPos < newBp ? newDisplayPos : newDisplayPos - 1;
                                          if (newInternal !== flat.waiting.position) {
                                              this.onPositionChange(context.row, newInternal);
                                          } else {
                                              this.markDirty(entry.groupKey);
                                              this.rebuildTableRows();
                                          }
                                          return;
                                      }
                                      const internalPos =
                                          locationRequested && newDisplayPos > bp ? newDisplayPos - 1 : newDisplayPos;
                                      this.onPositionChange(context.row, internalPos);
                                  },
                              },
                          }
                        : { text: String(displayPos()) },
                    ...(this.canEditWaitlist()
                        ? this.hasSpaceReservation(w.rsrvId, entry.spaceId)
                            ? { delete: { text: "-", component: undefined } }
                            : {}
                        : { delete: { text: "", component: undefined } }),
                    isRequest:
                        w.notifyId > 0
                            ? {
                                  text: "Yes",
                                  className: "waitlist-tooltip-request",
                              }
                            : {
                                  text: "No",
                                  className: "waitlist-tooltip-no-request",
                              },
                },
            };
        });

        if (!blockingAtOne) {
            const blockingDetail = this.eventDetailMap.get(entry.blockingEventId);
            const blockingRsrv = this.reservationMap.get(entry.blockingRsrvId);
            const blockingLocator = blockingRsrv?.event_locator ?? blockingDetail?.eventLocator ?? "";
            const blockingStartDt = blockingRsrv?.event_start_dt ?? "";
            const blockingEndDt = blockingRsrv?.event_end_dt ?? "";
            const blockingAsWaiting: WaitingEntry = {
                rsrvId: entry.blockingRsrvId,
                eventId: entry.blockingEventId,
                eventName: entry.blockingEventName,
                eventLocator: blockingLocator,
                position: 0,
                startDt: blockingStartDt,
                endDt: blockingEndDt,
                isRequest: false,
                notifyId: 0,
            };
            const blockingChildRow: Table.Row = {
                id: `blocking-${entry.groupKey}-${entry.blockingRsrvId}`,
                name: entry.blockingEventName,
                data: { groupKey: entry.groupKey, entry, waiting: blockingAsWaiting } as FlatWaitingRow,
                cells: {
                    eventName: {
                        component: S25ItemComponent,
                        inputs: {
                            includeTypeIcon: true,
                            itemTypeId: Item.Ids.Event,
                            itemId: entry.blockingEventId,
                            itemName: entry.blockingEventName,
                        },
                        textValue: entry.blockingEventName,
                    },
                    eventLocator: { text: blockingLocator },
                    startDt: this.waitlistDateCell(blockingStartDt || blockingAsWaiting.startDt, dateTimeFormat),
                    endDt: this.waitlistDateCell(blockingEndDt || blockingAsWaiting.endDt, dateTimeFormat),
                    scheduler: { text: blockingDetail?.scheduler ?? "" },
                    requestor: { text: blockingDetail?.requestor ?? "" },
                    organization: { text: blockingDetail?.organization ?? "" },
                    position: this.canEditWaitlist()
                        ? {
                              component: WaitlistPositionEditorComponent,
                              inputs: {
                                  position: computed(() => {
                                      this.dirtyGroupKeys();
                                      return this.blockingEventPositions.get(entry.groupKey) ?? 1;
                                  }),
                                  maxPosition: totalPositions,
                              },
                              outputs: {
                                  positionChange: (newPos: number) => this.onBlockingPositionChange(entry, newPos),
                              },
                          }
                        : { text: String(blockingPos) },
                    ...(this.canEditWaitlist()
                        ? entry.waitlistId == null
                            ? { delete: { text: "-", component: undefined } }
                            : {}
                        : { delete: { text: "", component: undefined } }),
                    isRequest: { text: "" },
                },
            };
            const insertIdx = Math.max(0, blockingPos - 2);
            childRows.splice(insertIdx, 0, blockingChildRow);
        }

        return [headerRow, ...childRows];
    }

    onDateChange(date: Date) {
        this.firstDateModel.set(date);
        this.tableComponent()?.refresh();
    }

    onWeeksChange(weeks: number) {
        this.weeksModel.set(weeks);
        TelemetryService.sendWithSub("Waitlist", "onWeeksChange", "week" + weeks);
        this.tableComponent()?.refresh();
    }

    onBlockingPositionChange(entry: WaitlistEntry, newPos: number) {
        this.blockingEventPositions.set(entry.groupKey, newPos);
        this.markDirty(entry.groupKey);
        this.rebuildTableRows();
    }

    private onHeaderPositionChange(entry: WaitlistEntry, headerWaiting: WaitingEntry, newInternalPos: number) {
        if (!entry.waiting) return;
        const waiting = entry.waiting;
        const oldPosition = headerWaiting.position;
        newInternalPos = S25Util.clamp(newInternalPos, 1, waiting.length);
        if (newInternalPos === oldPosition) {
            this.rebuildTableRows();
            return;
        }

        waiting.sort((a, b) => a.position - b.position);
        const itemIdx = waiting.findIndex((w) => w.rsrvId === headerWaiting.rsrvId);
        if (itemIdx < 0) return;
        const [item] = waiting.splice(itemIdx, 1);
        waiting.splice(newInternalPos - 1, 0, item);
        waiting.forEach((w, i) => (w.position = i + 1));

        this.markDirty(entry.groupKey);
        this.rebuildTableRows();
    }

    onPositionChange(row: Table.Row, newPosition: number) {
        const flat = row.data as FlatWaitingRow;
        const entry = this.cachedEntries.find((e) => e.groupKey === flat.groupKey);
        if (!entry?.waiting) return;

        const waiting = entry.waiting;
        const oldPosition = flat.waiting.position;
        newPosition = S25Util.clamp(newPosition, 1, waiting.length);
        if (newPosition === oldPosition) return;

        const tableHost = this.getTableHostElement();
        const rowElBefore = tableHost
            ? findWaitlistWaitingRowElement(tableHost, flat.groupKey, flat.waiting.rsrvId)
            : null;
        const rectBeforeReorder = rowElBefore?.getBoundingClientRect() ?? null;

        waiting.sort((a, b) => a.position - b.position);
        const itemIdx = waiting.findIndex((w) => w.rsrvId === flat.waiting.rsrvId);
        if (itemIdx < 0) return;
        const [item] = waiting.splice(itemIdx, 1);
        waiting.splice(newPosition - 1, 0, item);
        waiting.forEach((w, i) => (w.position = i + 1));

        this.markDirty(flat.groupKey);
        const moveGeneration = ++this.waitlistRowMoveAnimGeneration;
        this.rebuildTableRows();

        if (rectBeforeReorder && tableHost) {
            this.playWaitlistRowReorderFlip(
                tableHost,
                flat.groupKey,
                flat.waiting.rsrvId,
                rectBeforeReorder,
                moveGeneration,
            );
        }
    }

    private getTableHostElement(): HTMLElement | null {
        const table = this.tableComponent();
        if (!table) return null;
        return (table as unknown as { elementRef: { nativeElement: HTMLElement } }).elementRef.nativeElement;
    }

    private rebuildTableRows() {
        const table = this.tableComponent();
        if (!table) return;
        table.rows = this.cachedEntries.flatMap(this.mapEntryToRows);
        table.applyFilter(table.filterValue);
        table.detectChanges();
    }

    private playWaitlistRowReorderFlip(
        tableHost: HTMLElement,
        groupKey: string,
        rsrvId: number,
        rectBeforeReorder: DOMRect,
        moveGeneration: number,
    ) {
        requestAnimationFrame(() => {
            if (moveGeneration !== this.waitlistRowMoveAnimGeneration) return;
            const row = findWaitlistWaitingRowElement(tableHost, groupKey, rsrvId);
            if (!row) return;

            const rectAfterReorder = row.getBoundingClientRect();
            const dx = rectBeforeReorder.left - rectAfterReorder.left;
            const dy = rectBeforeReorder.top - rectAfterReorder.top;
            const cells = Array.from(row.querySelectorAll<HTMLElement>("th, td"));
            if (!cells.length) return;

            if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;

            for (const cell of cells) {
                cell.style.transition = "transform 0s";
                cell.style.transform = `translate(${dx}px, ${dy}px)`;
            }
            row.classList.add("waitlist-row-flipping");

            requestAnimationFrame(() => {
                if (moveGeneration !== this.waitlistRowMoveAnimGeneration) {
                    for (const cell of cells) {
                        cell.style.transition = "";
                        cell.style.transform = "";
                    }
                    row.classList.remove("waitlist-row-flipping");
                    return;
                }
                for (const cell of cells) {
                    cell.style.transition = "transform 0.42s cubic-bezier(0.25, 0.85, 0.3, 1)";
                    cell.style.transform = "translate(0, 0)";
                }

                let finished = false;
                const finishFlip = () => {
                    if (finished) return;
                    finished = true;
                    for (const cell of cells) {
                        cell.style.transition = "";
                        cell.style.transform = "";
                    }
                    row.classList.remove("waitlist-row-flipping");
                };

                cells[cells.length - 1]!.addEventListener("transitionend", finishFlip, { once: true });
                window.setTimeout(finishFlip, 480);
            });
        });
    }

    async savePositionOrder(entry: WaitlistEntry): Promise<void> {
        if (entry.waitlistId) {
            return this.reorderWaitlist(entry);
        }
        const request = this.buildCreateWaitlistRequest(entry);
        await this.waitlistStore.createWaitlistEntry({ request: { waitlists: [request] } });
        this.clearDirty(entry.groupKey);
        this.blockingEventPositions.delete(entry.groupKey);
        this.tableComponent()?.refresh();
    }

    /**
     * Aligns POST /waitlist/list.json with {@link reorderWaitlist}: for a competing (location-requested) slot,
     * the reservation that holds the space may be the first waiting row after reorder, not the original blocking row.
     */
    private buildCreateWaitlistRequest(entry: WaitlistEntry): WaitlistRequest {
        const spaceId = S25Util.parseInt(this.itemId() ?? entry.spaceId) || entry.spaceId;
        const sorted = [...(entry.waiting ?? [])].sort((a, b) => a.position - b.position);
        const isRequested = this.isBlockingLocationAssigned(entry) === false;
        const blockingPos = isRequested ? (this.blockingEventPositions.get(entry.groupKey) ?? 1) : 1;
        const newBlockingEntry = isRequested && blockingPos !== 1 ? sorted[0] : null;
        const notifyIdByRsrvId = this.buildNotifyIdByRsrvId(entry, newBlockingEntry?.rsrvId);

        if (!isRequested || blockingPos === 1) {
            return {
                spaceId,
                ...(entry.blockingRsrvId ? { blockingRsrvId: entry.blockingRsrvId } : {}),
                ...(entry.blockingEventId ? { blockingEventId: entry.blockingEventId } : {}),
                waiting: sorted.map((waiting) =>
                    this.waitingEntryRequestForRsrv(
                        entry,
                        waiting.rsrvId,
                        waiting.position,
                        notifyIdByRsrvId,
                        newBlockingEntry?.rsrvId,
                    ),
                ),
            };
        }

        let sortedRsrvIds = sorted.map((w) => w.rsrvId);
        sortedRsrvIds = sortedRsrvIds.filter((id) => id !== newBlockingEntry!.rsrvId);
        sortedRsrvIds.splice(blockingPos - 2, 0, entry.blockingRsrvId);

        const waiting = sortedRsrvIds.map((rsrvId, idx) =>
            this.waitingEntryRequestForRsrv(entry, rsrvId, idx + 1, notifyIdByRsrvId, newBlockingEntry!.rsrvId),
        );

        return {
            spaceId,
            blockingRsrvId: newBlockingEntry!.rsrvId,
            blockingEventId: newBlockingEntry!.eventId,
            waiting,
        };
    }

    private buildNotifyIdByRsrvId(entry: WaitlistEntry, excludedBlockingRsrvId?: number): Map<number, number> {
        const notifyIdByRsrvId = new Map<number, number>();
        for (const waiting of entry.waiting ?? []) {
            if (waiting.rsrvId === excludedBlockingRsrvId) continue;
            if (waiting.notifyId != null && waiting.notifyId > 0) {
                notifyIdByRsrvId.set(waiting.rsrvId, waiting.notifyId);
            }
        }
        return notifyIdByRsrvId;
    }

    private waitingEntryRequestForRsrv(
        entry: WaitlistEntry,
        rsrvId: number,
        position: number,
        notifyIdByRsrvId: Map<number, number>,
        excludedBlockingRsrvId?: number,
    ): WaitingEntryRequest {
        const fromWaiting = entry.waiting?.find((w) => w.rsrvId === rsrvId);
        const notifyId =
            rsrvId === excludedBlockingRsrvId ? null : (fromWaiting?.notifyId ?? notifyIdByRsrvId.get(rsrvId));
        if (fromWaiting) {
            return {
                rsrvId,
                position,
                conflictStartDt: this.formatWaitlistConflictDt(fromWaiting.startDt),
                conflictEndDt: this.formatWaitlistConflictDt(fromWaiting.endDt),
                ...this.notifyIdForCreatePayload(notifyId),
            };
        }
        if (rsrvId === entry.blockingRsrvId) {
            const reservation = this.reservationMap.get(rsrvId);
            return {
                rsrvId,
                position,
                conflictStartDt: this.formatWaitlistConflictDt(reservation?.event_start_dt),
                conflictEndDt: this.formatWaitlistConflictDt(reservation?.event_end_dt),
                ...this.notifyIdForCreatePayload(notifyId),
            };
        }
        return { rsrvId, position, ...this.notifyIdForCreatePayload(notifyId) };
    }

    private notifyIdForCreatePayload(notifyId: number | null | undefined): Pick<WaitingEntryRequest, "notifyId"> {
        return notifyId != null ? { notifyId } : {};
    }

    private buildReorderWaitingEntries(
        entry: WaitlistEntry,
        sortedRsrvIds: number[],
        blockingRsrvId: number,
    ): WaitlistReorderWaitingEntry[] {
        const notifyIdByRsrvId = this.buildNotifyIdByRsrvId(entry, blockingRsrvId);
        return sortedRsrvIds.map((rsrvId) => ({
            rsrvId,
            notifyId: this.notifyIdForReorderPayload(rsrvId === blockingRsrvId ? null : notifyIdByRsrvId.get(rsrvId)),
        }));
    }

    private notifyIdForReorderPayload(notifyId: number | null | undefined): number | null {
        return notifyId != null && notifyId > 0 ? notifyId : null;
    }

    private waitlistDateCell(rawDt: string | Date | undefined | null, dateTimeFormat: string): Table.Cell {
        if (rawDt == null || rawDt === "") return { text: "" };
        const normalized = S25Util.date.dropTZString(rawDt);
        if (!normalized) return { text: "" };
        return {
            text: S25Datefilter.transform(normalized, dateTimeFormat),
            sortValue: S25Util.date.toS25ISODateTimeStr(rawDt) ?? normalized,
        };
    }

    private formatWaitlistConflictDt(dt: string | Date | undefined | null): string | undefined {
        if (dt == null || dt === "") return undefined;
        return S25Util.date.toS25ISODateTimeStr(S25Util.date.dropTZString(dt)) ?? undefined;
    }

    private async reorderWaitlist(entry: WaitlistEntry): Promise<void> {
        const isRequested = this.isBlockingLocationAssigned(entry) === false;
        const blockingPos = isRequested ? (this.blockingEventPositions.get(entry.groupKey) ?? 1) : 1;
        const sorted = [...(entry.waiting ?? [])].sort((a, b) => a.position - b.position);
        const newBlockingEntry = isRequested && blockingPos !== 1 ? sorted[0] : null;

        let sortedRsrvIds = sorted.map((w) => w.rsrvId);
        if (newBlockingEntry) {
            sortedRsrvIds = sortedRsrvIds.filter((id) => id !== newBlockingEntry.rsrvId);
            sortedRsrvIds.splice(blockingPos - 2, 0, entry.blockingRsrvId);
        }

        const newBlockingRsrvId = newBlockingEntry?.rsrvId ?? entry.blockingRsrvId;
        const newBlockingEventId = newBlockingEntry?.eventId ?? entry.blockingEventId;

        await this.waitlistStore.reorderWaitlist({
            waitlistId: entry.waitlistId!,
            ...(isRequested
                ? {
                      blockingRsrvId: newBlockingRsrvId,
                      blockingEventId: newBlockingEventId,
                  }
                : {}),
            waiting: this.buildReorderWaitingEntries(entry, sortedRsrvIds, newBlockingRsrvId),
        });

        TelemetryService.sendWithSub("Waitlist", "Action", "reOrder");
        this.clearDirty(entry.groupKey);
        this.blockingEventPositions.delete(entry.groupKey);
        this.tableComponent()?.refresh();
    }

    openAddEventModal(entry: WaitlistEntry) {
        this.activeWaitlistEntry = entry;
        this.eventLocatorInput.set("");
        this.searchError.set("");
        this.searchSuccess.set("");
        this.foundEvent.set(null);
        this.addEventModal()?.open();
    }

    @Bind
    async searchEventByLocator() {
        const locator = this.eventLocatorInput().trim();
        if (!locator) {
            this.searchError.set("Please enter an Event Locator.");
            this.foundEvent.set(null);
            return;
        }

        this.searchError.set("");
        this.searchSuccess.set("");
        this.foundEvent.set(null);

        const entry = this.activeWaitlistEntry;
        if (!entry) return;

        const [eventId, err] = await S25Util.Maybe(EventService.getEventIdByLocator(locator));

        if (!eventId || !!err) {
            this.searchError.set(
                "No event found for that reference, please make sure your event reference is correct.",
            );
            return;
        }

        const eventName = await EventService.getEventNameOrTitle(eventId);
        this.foundEvent.set({ id: eventId, name: eventName ?? `Event ${eventId}` });

        const eventData = await EventService.getEventBindings(eventId);
        if (!eventData) {
            this.searchError.set("Could not load event reservations.");
            return;
        }

        const reservations = EventService.extractReservations(eventData);

        const overlapping = reservations.filter((rsrv) => {
            const rsrvStart = S25Util.date.parse(S25Util.date.dropTZString(rsrv.event_start_dt)); //S25Util.date.parse(S25Util.date.dropTZString(
            const rsrvEnd = S25Util.date.parse(S25Util.date.dropTZString(rsrv.event_end_dt));
            return entry.waiting?.some((w) => {
                const wStart = S25Util.date.parse(w.startDt);
                const wEnd = S25Util.date.parse(w.endDt);
                return rsrvStart < wEnd && wStart < rsrvEnd;
            });
        });

        if (!overlapping.length) {
            this.searchError.set("No overlapping reservations found for this location and time range.");
            return;
        }

        for (const rsrv of overlapping) {
            const request: WaitlistCreatRequest = {
                request: {
                    waitlists: [
                        {
                            spaceId: entry.spaceId,
                            blockingEventId: entry.blockingEventId,
                            waiting: [
                                {
                                    rsrvId: rsrv.reservation_id,
                                    conflictStartDt: S25Util.date.toS25ISODateTimeStr(
                                        S25Util.date.dropTZString(rsrv.reservation_start_dt),
                                    )!,
                                    conflictEndDt: S25Util.date.toS25ISODateTimeStr(
                                        S25Util.date.dropTZString(rsrv.reservation_end_dt),
                                    )!,
                                },
                            ],
                        },
                    ],
                },
            };

            try {
                TelemetryService.sendWithSub("Waitlist", "Action", "AddEventToWaitlist");
                await this.waitlistStore.createWaitlistEntry(request);
            } catch (e) {
                this.searchError.set(S25Util.errorText(e, "Failed to add event to waitlist."));
                return;
            }
        }

        this.searchSuccess.set("Event added to waitlist successfully.");
        this.addEventModal()?.close();
        this.tableComponent()?.refresh();
    }

    async onDelete(row: Table.Row) {
        if (!this.canEditWaitlist()) return;
        const flat = row.data as FlatWaitingRow;
        this.deleteMessage.set(
            flat.waiting.notifyId > 0
                ? "Are you sure you want to remove this entry from the waitlist? This action will also cancel the request."
                : "Are you sure you want to remove this entry from the waitlist?",
        );
        const yes = await this.deleteModal()?.open();
        if (!yes) return;

        await this.waitlistStore.deleteWaitlistEntry(flat.entry.waitlistId!, flat.entry, flat.waiting.rsrvId);

        if (flat.waiting.notifyId > 0) {
            await EventMicroService.putEventReservation(flat.waiting.eventId, {
                items: [
                    {
                        kind: "event",
                        id: flat.waiting.eventId,
                        profiles: [
                            {
                                reservations: [
                                    {
                                        rsrvId: flat.waiting.rsrvId,
                                        state: Event.Reservation.States.Cancelled,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            });
        }

        //await TaskCompetingUtil.assignNextWaitlistEntry(flat.entry, flat.waiting.rsrvId); // comment out for now
        this.tableComponent()?.refresh();
    }
}
